import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    CorrectMembershipSettlementBody,
    getSettlementCategories,
    patchMembershipSettlementBasis,
    patchSettlementCategory,
    postSettlementCategory,
} from './api';
import { settlementClassificationKeys } from './keys';
import { getTemplateCategoryBulkJobs, postTemplateCategoryBulk, postTemplateCategoryBulkRevert } from './categoryBulk';
import { SettlementCategoriesState, SettlementCategoryMutationResponse } from './types';

/** 세션 현황 「수강권 열기」의 수강권 단건 조회 키(앞자리). 정정 뒤 무효화한다. */
export const SESSION_REPORT_MEMBERSHIP_INFO_QUERY_KEY = 'sessionReportMembershipInfo';

export const useSettlementCategories = ({ centerId, enabled = true }: { centerId: string; enabled?: boolean }) =>
    useQuery({
        queryKey: settlementClassificationKeys.categories(centerId),
        queryFn: ({ signal }) => getSettlementCategories(centerId, signal),
        // 전역 기본값이 enabled:false 라 반드시 명시한다.
        enabled: enabled && !!centerId,
    });

/** 추가·수정 응답의 전체 목록으로 캐시를 바로 바꾼다(다시 읽지 않아도 된다). */
const useApplyCategoriesResponse = (centerId: string) => {
    const queryClient = useQueryClient();
    return (res: SettlementCategoryMutationResponse | null) => {
        if (!res || !Array.isArray(res.categories)) {
            queryClient.invalidateQueries({ queryKey: settlementClassificationKeys.categories(centerId) });
            return;
        }
        queryClient.setQueryData<SettlementCategoriesState>(settlementClassificationKeys.categories(centerId), {
            available: true,
            categories: res.categories,
        });
    };
};

export const useAddSettlementCategory = (centerId: string) => {
    const apply = useApplyCategoriesResponse(centerId);
    return useMutation({
        mutationFn: (name: string) => postSettlementCategory({ centerId, name }),
        onSuccess: apply,
    });
};

export const useUpdateSettlementCategory = (centerId: string) => {
    const apply = useApplyCategoriesResponse(centerId);
    return useMutation({
        mutationFn: ({ key, body }: { key: string; body: { name?: string; archived?: boolean } }) =>
            patchSettlementCategory({ centerId, key, body }),
        onSuccess: apply,
    });
};

/** 정산 결과(지난 달 포함)를 다시 읽게 한다 — 수강권 분류가 바뀌면 정산은 그때그때 수강권을 읽는다. */
const useInvalidateSettlementResults = () => {
    const queryClient = useQueryClient();
    return () => {
        queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlementForMembershipQuery'] });
        queryClient.invalidateQueries({ queryKey: ['getClassFeeSettlement'] });
        queryClient.invalidateQueries({ queryKey: ['getSalaryCalcsQuery'] });
        // 세션 현황(센터 전체 집계라 무겁다)은 지금 다시 받지 않고 낡음 표시만 한다. 열려 있는 보고서 화면은 그대로이고,
        // 「다시 계산」을 누르거나 그 조회가 새로 마운트될 때 새로 계산된다(자동 갱신을 약속하지 않는다).
        queryClient.invalidateQueries({ queryKey: ['sessionReport', 'report'], refetchType: 'none' });
        // 세션 현황에서 연 수강권 정보 창의 단건 조회 — 닫았다 다시 열 때 옛 값이 보이지 않게(가벼운 조회라 바로 다시 받는다).
        queryClient.invalidateQueries({ queryKey: [SESSION_REPORT_MEMBERSHIP_INFO_QUERY_KEY] });
    };
};

/**
 * 수강권 정산 기준 정정. 정산 결과(지난 달 포함)가 바뀌므로 정산·급여 조회를 함께 무효화한다.
 * 서버는 이어진 수강권을 하나씩 고치므로 중간에 실패해도 일부는 바뀌었을 수 있다 — 성공·실패 모두 무효화한다.
 */
export const useCorrectMembershipSettlementBasis = (centerId: string) => {
    const invalidate = useInvalidateSettlementResults();
    return useMutation({
        mutationFn: ({ membershipId, body }: { membershipId: string; body: CorrectMembershipSettlementBody }) =>
            patchMembershipSettlementBasis({ centerId, membershipId, body }),
        onSettled: invalidate,
    });
};

/** 이 상품의 최근 일괄 작업(상품 모달의 한 줄). 열 때마다 새로 읽는다. */
export const useTemplateCategoryBulkJobs = ({
    centerId,
    templateId,
    enabled = true,
}: {
    centerId: string;
    templateId: string;
    /** 세션이 무효면 false — 조회하지 않는다. */
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: settlementClassificationKeys.bulkJobs(centerId, templateId),
        queryFn: ({ signal }) => getTemplateCategoryBulkJobs({ centerId, templateId, signal }),
        // 전역 기본값이 enabled:false·staleTime 1분이라 명시한다.
        enabled: enabled && !!centerId && !!templateId,
        staleTime: 0,
    });

/** 일괄 쓰기 뒤: 정산 결과와 그 상품의 작업 한 줄을 다시 읽는다. 실패해도 일부가 바뀌었을 수 있어 성공·실패 모두. */
const useInvalidateAfterBulk = (centerId: string, templateId: string) => {
    const queryClient = useQueryClient();
    const invalidateSettlement = useInvalidateSettlementResults();
    return () => {
        invalidateSettlement();
        queryClient.invalidateQueries({ queryKey: settlementClassificationKeys.bulkJobs(centerId, templateId) });
    };
};

/** 상품 정산 분류 일괄 적용. */
export const useApplyTemplateCategoryBulk = ({ centerId, templateId }: { centerId: string; templateId: string }) => {
    const invalidate = useInvalidateAfterBulk(centerId, templateId);
    return useMutation({
        mutationFn: ({ requestId, previewHash, signal }: { requestId: string; previewHash: string; signal?: AbortSignal }) =>
            postTemplateCategoryBulk({ centerId, templateId, requestId, previewHash, signal }),
        onSettled: invalidate,
    });
};

/** 일괄 적용 되돌리기. */
export const useRevertTemplateCategoryBulk = ({ centerId, templateId }: { centerId: string; templateId: string }) => {
    const invalidate = useInvalidateAfterBulk(centerId, templateId);
    return useMutation({
        mutationFn: ({ jobId, requestId, signal }: { jobId: string; requestId: string; signal?: AbortSignal }) =>
            postTemplateCategoryBulkRevert({ centerId, templateId, jobId, requestId, signal }),
        onSettled: invalidate,
    });
};
